import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Avatar, Menu } from 'antd';
import { Icon, Modal, Spin } from '@bluevision/components';
import { history, useAccess } from 'umi';
import type { MenuInfo } from 'rc-menu/lib/interface';
import { useTranslation } from 'react-i18next';
import { useModel } from '@@/plugin-model/useModel';

import ToggleRender from '@/components/Render/ToggleRender';
import { useIdentification } from '@/components/RealnameIdentification';
import { logout, queryCurrent } from '@/services/authority/login';
import { getVaGrayscale } from '@/services/offlinepayment';

import EnterpriseInformation from './EnterpriseInformation';
import MyInformation from './MyInformation';
import LanguageSelect from './LanguageSelect/index';
import IdentificationStatus from './IdentificationStatus';
import HeaderDropdown from '../HeaderDropdown';
import ChangePassword from '../ChangePassword/Content';

import styles from './index.less';
import { openWindowInSafe, useRequest } from '@bluevision/tools';
import { IRealNameIdentification } from '@/services/identification/interface';
import { IEnterpriseCertification } from '@/services/enterpriseCertification/interface';
import { getCertificationLink } from '@/services/enterpriseCertification';
import CertificationInformation from './CertificationInformation';
import { LocalStorageKey } from '@/data/constant';
import { resetPosthogUser } from '@/utils/posthog';

export type GlobalHeaderRightProps = {
  menu?: boolean;
};

enum DropdownMenuType {
  ENTERPRISE_INFORMATION = 'enterprise_information',
  USER_INFO = 'user_info',
  CHANGE_PASSWORD = 'change_password',
  REAL_NAME_AUTHENTICATION = 'real_name_authentication',
  ENTERPRISE_REAL_NAME_AUTHENTICATION = 'enterprise_real_name_authentication',
  ENTERPRISE_AUTHENTICATION = 'enterprise_authentication',
  INFO = 'info',
  LOGOUT = 'logout',
}

/**
 * 退出登录，并且将当前的 url 保存
 */
const loginOut = async () => {
  await logout();

  resetPosthogUser();

  // 清除用户信息缓存，防止数据泄露
  localStorage.removeItem(LocalStorageKey.CACHED_USER_INFO);
  const { query = {} } = history.location;
  const { redirect } = query;
  // Note: There may be security issues, please note
  if (window.location.pathname !== '/user/login' && !redirect) {
    history.replace({
      pathname: '/user/login',
    });
  }
};

const AvatarDropdown: React.FC<GlobalHeaderRightProps> = () => {
  const { t } = useTranslation();
  const { openModal } = useIdentification();

  const { initialState, setInitialState } = useModel('@@initialState');

  const { isTeamUser } = useAccess();

  const [menuShow, setMenuShow] = useState(false);
  const [visible, setVisible] = useState(false);
  const [showEnterprise, setShowEnterprise] = useState(false);
  const [showMy, setShowMy] = useState(false);

  // 实名认证灰度
  const [identificationGray, setIdentificationGray] = useState(false);
  // 企业认证灰度
  const [signCertificationGray, setSignCertificationGray] = useState(false);

  // 用户信息
  const [userInfo, setUserInfo] = useState(initialState?.currentUser);
  const { request, loading: userInfoLoading } = useRequest(queryCurrent, (res) => {
    setUserInfo(res);

    // 如果展示企业认证信息，则不需要展示实名认证信息
    setSignCertificationGray(Boolean(res?.signCertificationShow));

    // 不需要展示企业认证信息，则获取实名认证灰度信息
    if (!res?.signCertificationShow) {
      getVaGrayscale().then(([grayRes]) => {
        setIdentificationGray(Boolean(grayRes?.real_name_grayscale));
      });
    }
  });
  // 获取认证链接
  const { request: getCertificationLinkRequest } = useRequest(getCertificationLink, (res) => {
    if (res?.short_link) {
      openWindowInSafe(res.short_link, '_self');
    }
  });

  useEffect(() => {
    if (menuShow) {
      // 每次打开菜单都实时请求用户信息，不使用缓存
      request();
    }
    // 不依赖request, 会死循环
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuShow]);
  // 企业认证/企业实名认证信息
  const [certificationShow, setCertificationShow] = useState(false);
  const [certificationType, setCertificationType] =
    useState<IEnterpriseCertification.Common.CertificationType>();

  const onMenuClick = useCallback(
    (event: MenuInfo) => {
      const { key } = event;
      // 退出登录
      if (key === DropdownMenuType.LOGOUT) {
        Modal.openAsync({
          title: t('quit'),
          type: 'info',
          children: t('confirm_to_exit_the_current_account'),
          okText: t('OK'),
          cancelText: t('cancel'),
          maskClosable: false,
          onOk: async () => {
            await setInitialState((s) => ({ ...s, currentUser: undefined })).then((r) => r);
            await loginOut();
          },
        });

        return;
      }

      // 企业信息
      if (key === DropdownMenuType.ENTERPRISE_INFORMATION) {
        setShowEnterprise(true);
      }

      // 我的信息
      if (key === DropdownMenuType.USER_INFO) {
        setShowMy(true);
      }

      // 修改密码
      if (key === DropdownMenuType.CHANGE_PASSWORD) {
        setVisible(true);
      }

      // 实名认证
      if (key === DropdownMenuType.REAL_NAME_AUTHENTICATION) {
        openModal(() => {}, {
          skipIdentificationCheck: true,
        });
      }

      // 企业实名认证
      if (key === DropdownMenuType.ENTERPRISE_REAL_NAME_AUTHENTICATION) {
        // 已认证
        if (
          userInfo?.signIndividualInfo?.certificationStatus ===
          IEnterpriseCertification.Common.EnterpriseCertificationStatus.CERTIFIED
        ) {
          setCertificationShow(true);
          setCertificationType(IEnterpriseCertification.Common.CertificationType.INDIVIDUAL);
        } else {
          // 未认证
          // 获取认证链接
          getCertificationLinkRequest({
            certification_type: IEnterpriseCertification.Common.CertificationType.INDIVIDUAL,
            redirect_url: window.location.href,
          });
        }
      }

      // 企业认证
      if (key === DropdownMenuType.ENTERPRISE_AUTHENTICATION) {
        // 已认证
        if (
          userInfo?.signEnterpriseInfo?.certificationStatus ===
          IEnterpriseCertification.Common.EnterpriseCertificationStatus.CERTIFIED
        ) {
          setCertificationShow(true);
          setCertificationType(IEnterpriseCertification.Common.CertificationType.ENTERPRISE);
        } else {
          // 未认证
          // 获取认证链接
          getCertificationLinkRequest({
            certification_type: IEnterpriseCertification.Common.CertificationType.ENTERPRISE,
            redirect_url: window.location.href,
          });
        }
      }

      if (key === DropdownMenuType.INFO) {
        history.push('/info');
      }
    },
    // 不依赖getCertificationLinkRequest, 会死循环
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openModal, setInitialState, t, userInfo],
  );

  const onCloseMy = () => {
    setShowMy(false);
  };

  const loading = (
    <span className={`${styles.action} ${styles.account}`}>
      <Spin size='small' style={{ marginLeft: 8, marginRight: 8 }} />
    </span>
  );

  const menuHeaderDropdown = useMemo(() => {
    return (
      <Menu className={styles.menu} selectedKeys={[]} onClick={onMenuClick}>
        {userInfoLoading ? (
          <Spin size='small' style={{ height: 280 }} />
        ) : (
          <>
            {/* 开通团队管理 && 团队用户 不可见 */}
            {!isTeamUser && (
              <>
                <Menu.Item key={DropdownMenuType.ENTERPRISE_INFORMATION}>
                  <Icon name='enterprise' className={styles.icon} />
                  {t('enterprise_information')}
                  <ToggleRender visible={showEnterprise}>
                    <EnterpriseInformation onClose={() => setShowEnterprise(false)} />
                  </ToggleRender>
                </Menu.Item>
                <Menu.Item key={DropdownMenuType.USER_INFO}>
                  <Icon name='user' className={styles.icon} />
                  {t('my_information')}
                  <ToggleRender visible={showMy}>
                    <MyInformation onClose={onCloseMy} />
                  </ToggleRender>
                </Menu.Item>
              </>
            )}

            {/* 企业认证优先级高于实名认证。企业认证灰度开启，则不显示实名认证；企业认证灰度未开启，则判断实名认证 */}
            {!signCertificationGray && identificationGray && (
              <Menu.Item key={DropdownMenuType.REAL_NAME_AUTHENTICATION}>
                <div className={styles['identify-wrapper']}>
                  <Icon name='authentication' className={styles.icon} />
                  <IdentificationStatus
                    authenticationName={t('real_name_authentication_simple')}
                    loading={userInfoLoading}
                    isVerified={
                      userInfo?.verifyInfo?.verifyStatus ===
                      IRealNameIdentification.Common.IdentificationStatus.VERIFIED
                    }
                  />
                </div>
              </Menu.Item>
            )}

            {/* 企业认证 并且 非团队用户 */}
            {signCertificationGray && !isTeamUser && (
              <>
                <Menu.Item key={DropdownMenuType.ENTERPRISE_REAL_NAME_AUTHENTICATION}>
                  <div className={styles['identify-wrapper']}>
                    <Icon name='authentication' className={styles.icon} />
                    <IdentificationStatus
                      authenticationName={t('real_name_authentication_simple')}
                      loading={userInfoLoading}
                      isVerified={
                        userInfo?.signIndividualInfo?.certificationStatus ===
                        IEnterpriseCertification.Common.EnterpriseCertificationStatus.CERTIFIED
                      }
                    />
                  </div>
                </Menu.Item>
                <Menu.Item key={DropdownMenuType.ENTERPRISE_AUTHENTICATION}>
                  <div className={styles['identify-wrapper']}>
                    <Icon name='premium' className={styles.icon} />
                    <IdentificationStatus
                      authenticationName={t('enterprise_authentication_menu')}
                      loading={userInfoLoading}
                      isVerified={
                        userInfo?.signEnterpriseInfo?.certificationStatus ===
                        IEnterpriseCertification.Common.EnterpriseCertificationStatus.CERTIFIED
                      }
                    />
                  </div>
                </Menu.Item>
                <CertificationInformation
                  show={certificationShow}
                  certificationType={certificationType}
                  userInfo={userInfo}
                  onClose={() => {
                    setCertificationShow(false);
                    setCertificationType(undefined);
                  }}
                />
              </>
            )}

            <Menu.Item key={DropdownMenuType.CHANGE_PASSWORD}>
              <Icon name='lock' className={styles.icon} />
              {t('change_password')}
              <ChangePassword
                visible={visible}
                onClose={(success?: unknown) => {
                  setVisible(false);
                  if (success) {
                    setInitialState((s) => ({ ...s, currentUser: undefined })).then((r) => r);
                    history.replace({ pathname: '/user/login' });
                  }
                }}
              />
            </Menu.Item>
            <Menu.Item>
              <LanguageSelect />
            </Menu.Item>
            <Menu.Item key={DropdownMenuType.LOGOUT}>
              <Icon name='quit' className={styles.icon} />
              {t('logout')}
            </Menu.Item>
          </>
        )}
      </Menu>
    );
  }, [
    onMenuClick,
    isTeamUser,
    t,
    showEnterprise,
    showMy,
    signCertificationGray,
    identificationGray,
    userInfoLoading,
    userInfo,
    certificationShow,
    certificationType,
    visible,
    setInitialState,
  ]);

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  if (!currentUser || !currentUser.username) {
    return loading;
  }

  return (
    <HeaderDropdown
      overlay={menuHeaderDropdown}
      trigger={['hover']}
      onOpenChange={(flag) => setMenuShow(flag)}
    >
      <div className={`${styles.action} ${styles.account}`}>
        <Avatar className={styles.avatar}>
          {(currentUser?.userInfo?.contactName ?? currentUser?.username)?.slice(0, 1)}
        </Avatar>
        <div className={styles['user-info-wrapper']}>
          {/* 根据用户角色和团队管理权限决定显示团队名称还是公司名称 */}
          {isTeamUser
            ? // 当团队管理功能开启且用户角色为TEAM_USER时，显示团队名称
              currentUser?.teamInfo?.teamName && (
                <div className={styles.company}>{currentUser?.teamInfo?.teamName}</div>
              )
            : // 其他所有情况下显示公司名称
              currentUser?.companyInfo?.companyName && (
                <div className={styles.company}>{currentUser?.companyInfo?.companyName}</div>
              )}
          <div className={styles.name}>{currentUser.username}</div>
        </div>
      </div>
    </HeaderDropdown>
  );
};

export default AvatarDropdown;
